import { useMemo } from 'react'
import { NavigableModal } from '$components'
import { LoaderIcon } from '$components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { serviceShowOptions, serviceShowQueryKey, serviceVariantSyncMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import { ToastType } from '$components'
import { EditPricingForm, type EditPricingSchema, Input } from '$features/services/forms/edit-pricing'
import { ServiceRoutePath } from '../../routes/protected'
import { Router } from '$utils/router'
import { DogBreed } from '$api-client/types.gen'

export function EditPricingModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const serviceId = Number(id)
  const queryClient = useQueryClient()

  const handleClose = () => navigate(Router.getRoute(ServiceRoutePath.ServicesView, { id: serviceId }))

  const { data: service, isLoading: isLoadingService } = useQuery({
    ...serviceShowOptions({
      path: {
        id: serviceId,
      },
      query: {
        include_deleted: true,
      },
    }),
  })

  const defaultValues = useMemo(() => {
    if (!service?.data.values?.variants) {
      return null
    }

    return {
      [Input.Variants]: service.data.values.variants.map((variant) => ({
        is_default: variant.is_default,
        size_category: variant.size_category_id
          ? { id: variant.size_category_id, value: variant.size_category?.values?.name ?? variant.size_category_id }
          : null,
        coat_type: variant.coat_type_id
          ? { id: variant.coat_type_id, value: variant.coat_type?.values?.name ?? variant.coat_type_id }
          : null,
        breed: variant.breed
          ? { id: variant.breed, value: variant.breed }
          : null,
        price: variant.price_in_cents / 100,
        duration: variant.duration,
      })),
    } as EditPricingSchema
  }, [service?.data.values?.variants])

  const { mutate: syncVariants, isPending: isSyncingVariants } = useMutation({
    ...serviceVariantSyncMutation(),
    onSuccess: () => {
      ToastHelper.updateSuccess(ToastType.ServiceVariant)
      queryClient.invalidateQueries({
        queryKey: serviceShowQueryKey({
          path: {
            id: serviceId,
          },
        }),
      })
      handleClose()
    },
    onError: () => ToastHelper.updateError(ToastType.ServiceVariant),
  })

  const handleSubmit = (values: EditPricingSchema) => {
    syncVariants({
      path: {
        serviceId,
      },
      body: {
        variants: values[Input.Variants].map((variant) => ({
          is_default: variant.is_default ?? false,
          size_category_id: Number(variant.size_category?.id),
          coat_type_id: Number(variant.coat_type?.id),
          breed: variant.breed?.value as DogBreed | undefined,
          price_in_cents: Number(variant.price) * 100,
          duration: Number(variant.duration),
        })),
      },
    })
  }

  if (!defaultValues || isLoadingService) {
    return (
      <NavigableModal onClose={handleClose}>
        <div className="w-[640px] h-96 flex items-center justify-center">
          <LoaderIcon />
        </div>
      </NavigableModal>
    )
  }

  return (
    <EditPricingForm
      isLoading={isSyncingVariants}
      onSubmit={handleSubmit}
      onCancel={handleClose}
      currency={service?.data?.values?.currency}
      defaultValues={defaultValues}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Pricing
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <EditPricingForm.Content currency={service?.data?.values?.currency} />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <EditPricingForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </EditPricingForm>
  )
}
